// sendContext — the household a Send surface is being opened FOR (sol finding 10).
//
// THE PROBLEM. "Send…" on a household row used to link to a bare `/send`, and
// W6-NOTES said so out loud: "None of the send surfaces takes a household in its
// URL today, so a deep link per document type would land staff on a picker with
// their household unselected." That is true of the surfaces as they were, and it
// made the most common operation on the page — send this household something —
// start by finding them again in a list they had just been looking at.
//
// THE FIX is this module: one encoding of "who this is for", written into the
// hub's URL and carried forward onto whichever surface the operator picks. Each
// surface takes what it can use and ignores the rest:
//
//   /send                  shows who it is for, and forwards the context
//   /send/payment-plan     preselects the property and the unit
//   /send/deposit          preselects the occupancy on that unit
//   /applications/:g/lease already addressed by group — the Lease tile deep-links
//
// WHAT TRAVELS, AND WHY IT IS SAFE. Identifiers the SPA already puts in URLs
// (`groupId` is in every builder link; `householdId` is a route param), plus the
// household's NAME so the banner can say who without a second request. This is a
// staff-only app behind Keycloak; the name is already on the screen the link was
// clicked from. Nothing else travels — no email, no answers, and nothing about
// an accommodation.
//
// WHAT IS NOT SHOWN. The ids are plumbing: the banner renders the name and the
// address, never `groupId`. A raw id on a staff screen is noise, and it is the
// rule the whole Sent Items surface is built on.

export interface SendContext {
  /** `<caseId>` or `group:<groupId>` — the household file this came from. */
  householdId?: string;
  /** The application group, when the household has one. */
  groupId?: string;
  /** AppFolio ids, for the pickers. */
  propertyId?: string;
  unitId?: string;
  /** The people, comma-joined — so the banner can name them with no fetch. */
  name?: string;
  /** "Larkspur Court · 412 NE Fern St #7" — the same, for the banner's second line. */
  where?: string;
}

const KEYS = ['householdId', 'groupId', 'propertyId', 'unitId', 'name', 'where'] as const;

/** A context with nothing in it — `/send` reached from the nav. */
export function isEmptySendContext(ctx: SendContext): boolean {
  return KEYS.every((k) => !ctx[k]);
}

/**
 * The context a URL carries.
 *
 * Empty strings are dropped rather than kept: `?name=` is not "a household
 * called nothing", it is a malformed link, and the honest response to one is
 * the plain surface.
 */
export function sendContextFromSearch(search: URLSearchParams): SendContext {
  const ctx: SendContext = {};
  for (const key of KEYS) {
    const value = search.get(key);
    if (value && value.trim()) ctx[key] = value;
  }
  return ctx;
}

/** The inverse: `?householdId=…&groupId=…`, or '' when there is no context. */
export function sendContextQuery(ctx: SendContext): string {
  const sp = new URLSearchParams();
  for (const key of KEYS) {
    const value = ctx[key];
    if (value && value.trim()) sp.set(key, value);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * A destination with the context appended.
 *
 * `to` may already carry a query (nothing does today, and something will), so
 * the two are merged rather than concatenated — a second `?` would silently
 * break the link.
 */
export function withSendContext(to: string, ctx: SendContext): string {
  const query = sendContextQuery(ctx);
  if (!query) return to;
  const [path, existing] = to.split('?');
  if (!existing) return `${path}${query}`;
  const sp = new URLSearchParams(existing);
  for (const [k, v] of new URLSearchParams(query.slice(1))) sp.set(k, v);
  return `${path}?${sp.toString()}`;
}
